import React from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import {
  CameraAnalysisMetadata,
  CameraRequestMetadata,
  Message,
  PrescriptionMetadata,
} from '../types';
import { API_BASE_URL } from '../services/chatService';
import './MessageList.css';
import { Link } from 'react-router-dom';

const CAMERA_FOCUS_LABELS: Record<string, string> = {
  face: '面部',
  tongue: '舌苔',
  skin: '皮肤',
};

type MessageVoiceStatus = {
  status: 'idle' | 'loading' | 'ready' | 'playing' | 'error';
  error?: string;
};

interface MessageListProps {
  messages: Message[];
  onCameraCaptureRequest?: (metadata?: CameraRequestMetadata) => void;
  onCameraRequestDecline?: (metadata?: CameraRequestMetadata) => void;
  onForwardAnalysis?: (analysis: string, metadata?: CameraAnalysisMetadata) => void;
  onToggleVoicePlayback?: (message: Message, key: string) => void;
  voicePlaybackStatuses?: Record<string, MessageVoiceStatus>;
  activeVoiceKey?: string | null;
  getMessageKey?: (message: Message, index: number) => string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  onCameraCaptureRequest,
  onCameraRequestDecline,
  onForwardAnalysis,
  onToggleVoicePlayback,
  voicePlaybackStatuses,
  activeVoiceKey,
  getMessageKey,
}) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // 每次消息更新时滚动到底部
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 格式化时间戳
  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const renderMessageContent = (message: Message, key: string) => {
    const voiceStatus = voicePlaybackStatuses?.[key];
    const showVoiceButton =
      !!onToggleVoicePlayback && message.role === 'assistant' && Boolean(message.content);

    if (message.messageType === 'prescription') {
      const metadata = (message.metadata || {}) as PrescriptionMetadata;

      let linkHref =
        metadata.frontendUrl ||
        metadata.url ||
        metadata.backendUrl ||
        metadata.apiUrl;

      let prescriptionId = metadata.prescription_id;
      if (!prescriptionId && linkHref) {
        const idMatch = /prescriptions\/(\w{8,})/i.exec(linkHref);
        if (idMatch && idMatch[1]) {
          prescriptionId = idMatch[1];
        }
      }

      const fallbackApiUrl = prescriptionId
        ? `${API_BASE_URL}/herb-library/prescriptions/${prescriptionId}`
        : undefined;

      if (
        !metadata.frontendUrl &&
        linkHref &&
        /^https?:\/\/[^\s]+\/prescriptions\//.test(linkHref) &&
        typeof window !== 'undefined' &&
        prescriptionId
      ) {
        try {
          const candidateUrl = new URL(linkHref, window.location.origin);
          const queryString = `?source=${encodeURIComponent(
            metadata.apiUrl || fallbackApiUrl || candidateUrl.href,
          )}`;
          let normalizedPath = candidateUrl.pathname;
          if (normalizedPath.startsWith('/prescriptions/')) {
            normalizedPath = `/app${normalizedPath}`;
          }
          const replacementPath = `${normalizedPath}${queryString}`;
          linkHref = window.location.origin
            ? `${window.location.origin}${replacementPath}`
            : replacementPath;
        } catch (conversionError) {
          console.warn('处方链接格式化失败，将使用原始链接', conversionError);
        }
      }

      let internalPath: string | null = null;
      if (typeof window !== 'undefined' && linkHref) {
        if (linkHref.startsWith('/')) {
          internalPath = linkHref;
        } else {
          try {
            const candidateUrl = new URL(linkHref, window.location.origin);
            if (candidateUrl.origin === window.location.origin) {
              internalPath = `${candidateUrl.pathname}${candidateUrl.search}`;
            }
          } catch (error) {
            internalPath = null;
          }
        }
      }

      if (internalPath?.startsWith('/prescriptions/')) {
        internalPath = `/app${internalPath}`;
      }

      return (
        <div className="prescription-card">
          <div className="prescription-header">
            <span className="prescription-icon">🧾</span>
            <div>
              <p className="prescription-title">{metadata.title || 'AI生成处方'}</p>
              <p className="prescription-subtitle">药材库AI已完成配方</p>
            </div>
          </div>
          <div className="prescription-actions">
            {internalPath ? (
              <Link
                className="view-prescription-button"
                to={internalPath}
                state={{ from: '/app/chat' }}
              >
                查看处方
              </Link>
            ) : linkHref ? (
              <a
                className="view-prescription-button"
                href={linkHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                查看处方
              </a>
            ) : (
              <span className="prescription-disabled">链接暂不可用</span>
            )}
          </div>
        </div>
      );
    }


    if (message.messageType === 'camera_request') {
      const metadata = (message.metadata || {}) as CameraRequestMetadata | undefined;
      const instructions = metadata?.instructions ||
        'AI助手希望采集实时影像以辅助辨证，请在光线柔和的环境下拍摄正面照片。';
      return (
        <div className="camera-request-card">
          <div className="camera-request-header">
            <span className="camera-request-icon">🎥</span>
            <div>
              <p className="camera-request-title">需要开启摄像头采集</p>
              <p className="camera-request-sub">采集影像后我会结合图像继续辨证分析。</p>
            </div>
          </div>
          <div className="camera-request-body">
            <p>{instructions}</p>
            {metadata?.focus && (
              <span className="camera-request-focus">
                推荐拍摄部位：{metadata.focus === 'face' ? '面部正面' : metadata.focus === 'tongue' ? '舌苔' : '皮肤局部'}
              </span>
            )}
          </div>
          {(onCameraCaptureRequest || onCameraRequestDecline) && (
            <div className="camera-request-actions">
              {onCameraCaptureRequest && (
                <button
                  type="button"
                  className="camera-request-button"
                  onClick={() => onCameraCaptureRequest(metadata)}
                >
                  同意采集
                </button>
              )}
              {onCameraRequestDecline && (
                <button
                  type="button"
                  className="camera-request-button secondary"
                  onClick={() => onCameraRequestDecline(metadata)}
                >
                  暂不授权
                </button>
              )}
            </div>
          )}
        </div>
      );
    }

    if (message.messageType === 'image') {
      const metadata = (message.metadata || {}) as CameraRequestMetadata | undefined;
      const focusLabel = metadata?.focus ? CAMERA_FOCUS_LABELS[metadata.focus] ?? '影像' : '影像';
      const caption = message.content || `已上传${focusLabel}`;

      if (message.role !== 'assistant') {
        return (
          <div className="message-image-placeholder">
            <span className="message-image-placeholder__icon" aria-hidden>🗂️</span>
            <div className="message-image-placeholder__text">
              <strong>图像已提交</strong>
              <span>{caption}</span>
            </div>
          </div>
        );
      }

      return (
        <div className="message-image-card">
          {message.image_url && (
            <div className="message-image-preview">
              <img src={message.image_url} alt={caption} />
            </div>
          )}
          {caption && <p className="message-image-caption">{caption}</p>}
        </div>
      );
    }

    if (message.messageType === 'image_analysis') {
      const metadata = (message.metadata || {}) as CameraAnalysisMetadata | undefined;
      const forwarded = Boolean(metadata?.forwarded);
      return (
        <div className="camera-analysis-card">
          <div className="camera-analysis-header">
            <span className="camera-analysis-icon">🔍</span>
            <div>
              <p className="camera-analysis-title">影像分析结果</p>
              <p className="camera-analysis-sub">已根据上传影像给出中医辨证要点。</p>
            </div>
          </div>
          <div className="camera-analysis-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
              {message.content}
            </ReactMarkdown>
          </div>
          {onForwardAnalysis && (
            <div className="camera-analysis-actions">
              <button
                type="button"
                className={`camera-analysis-button ${forwarded ? 'disabled' : ''}`}
                onClick={() => !forwarded && onForwardAnalysis(message.content, metadata)}
                disabled={forwarded}
              >
                {forwarded ? '已同步至问诊流程' : '同步到问诊流程'}
              </button>
            </div>
          )}
        </div>
      );
    }

    const isAssistant = message.role === 'assistant';
    const hasMarkdown = isAssistant && !!message.content;

    return (
      <>
        {message.image_url && message.role === 'assistant' && (
          <div className="message-image-container">
            <img src={message.image_url} alt="用户上传" className="message-image" />
          </div>
        )}
        {hasMarkdown ? (
          <div className="message-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="message-text">{message.content}</div>
        )}
        {showVoiceButton && (
          <div className="message-voice-actions">
            <button
              type="button"
              className={`message-voice-button ${voiceStatus?.status ?? 'idle'}`.trim()}
              onClick={() => onToggleVoicePlayback?.(message, key)}
              disabled={voiceStatus?.status === 'loading'}
            >
              {voiceStatus?.status === 'loading'
                ? '生成语音…'
                : activeVoiceKey === key && voiceStatus?.status === 'playing'
                ? '停止播报'
                : '播报语音'}
            </button>
            {voiceStatus?.status === 'error' && voiceStatus.error && (
              <span className="message-voice-error">{voiceStatus.error}</span>
            )}
          </div>
        )}
      </>
    );
  };

  const buildKey = React.useCallback(
    (message: Message, index: number) => {
      if (getMessageKey) {
        return getMessageKey(message, index);
      }
      return `${message.timestamp ?? 'msg'}-${index}`;
    },
    [getMessageKey],
  );

  return (
    <div className="message-list">
      {messages.length === 0 ? (
        <div className="empty-messages">
          <p>暂无消息记录，请开始聊天！</p>
        </div>
      ) : (
        messages.map((message, index) => {
          const baseClass = message.role === 'user' ? 'user-message' : 'assistant-message';
          const typeClass = message.messageType ? `message-${message.messageType}` : '';
          const messageKey = buildKey(message, index);

          return (
            <motion.div
              key={messageKey}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={`message ${baseClass} ${typeClass}`.trim()}
            >
              <div className="message-content">
                <div className="message-header">
                  <span className="message-role">
                    {message.role === 'user' ? '用户' : 'AI助手'}
                  </span>
                  {message.timestamp && (
                    <span className="message-time">
                      {formatTimestamp(message.timestamp)}
                    </span>
                  )}
                </div>
                {renderMessageContent(message, messageKey)}
              </div>
            </motion.div>
          );
        })
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList; 